<template>
  <div
    class="status-changer"
    :class="{ 'status-changer_disabled': disabled }"
  >
    <Dropdown
      :visible="isDropdownVisible"
      :trigger="['click']"
      overlayClassName="status-changer__dropdown"
      :disabled="disabled"
      @visibleChange="onDropdownVisibleChange"
      @click.stop
    >
      <Status
        :text="currentState.title"
        :type="currentState.type"
        :hasBackground="hasBackground"
        :size="size"
        withOverflowTooltip
      >
        <DownSvg
          v-if="!disabled"
          class="status-changer__icon"
          :class="{ 'status-changer__icon_up': isDropdownVisible }"
        />
      </Status>

      <template #overlay>
        <div class="status-changer__panel">
          <Loadable
            class="status-changer__loadable"
            :isLoading="isLoading"
            :isDone="isDone"
          >
            <Menu>
              <div
                v-if="!availableStates.length"
                class="status-changer__empty-list"
              >
                {{ tt('statusChanger.emptyListText') }}
              </div>

              <MenuItem
                v-for="availableState in availableStates"
                :key="availableState.slug"
                :disabled="availableState.disabled"
                class="status-changer__menu-item"
              >
                <Popconfirm
                  v-if="availableState.showPopconfirm"
                  placement="left"
                  :title="availableState.textConfirm || textConfirm"
                  :okText="tt('shared.yes')"
                  :cancelText="tt('shared.no')"
                  :disabled="availableState.disabled"
                  @confirm="handleChangeState(availableState)"
                >
                  <Tooltip
                    class="status-changer__tooltip"
                    :visible="availableState.disabled"
                    :title="availableState.disabled ? tooltipText : ''"
                    :placement="EPopoverPlacement.left"
                  >
                    <Status
                      class="status-changer__menu-item-status"
                      :text="availableState.title"
                      :type="availableState.type"
                    />
                  </Tooltip>
                </Popconfirm>
                <Tooltip
                  v-else
                  :visible="availableState.disabled"
                  :title="availableState.disabled ? tooltipText : ''"
                  :placement="EPopoverPlacement.left"
                >
                  <Status
                    class="status-changer__menu-item-status"
                    :text="availableState.title"
                    :type="availableState.type"
                    @click="handleChangeState(availableState)"
                  />
                </Tooltip>
              </MenuItem>

              <slot name="action" />
            </Menu>
          </Loadable>
        </div>
      </template>
    </Dropdown>
  </div>
</template>

<script lang="ts">

import {
  Popconfirm,
  Dropdown,
  Menu,
  MenuItem,
} from 'ant-design-vue';
import {
  defineComponent,
  PropType,
} from 'vue';

import DownSvg from '@/assets/svg/16x16/chevron-down.svg';
import tt from '@/i18n/utils/translateText';
import { toLowercase } from '@/utils';
import { EPopoverPlacement, ESize } from '@/ui/types';

import Status from '../../Status/index.vue';
import Tooltip from '../../Tooltip/index.vue';
import { useDropdownVisibleChange } from './application/useDropdownVisibleChange';
import { TStatusChangerAvailableState, TStatusChangerCurrentState } from './domain/types';
import Loadable from '../Loadable/index.vue';

export default defineComponent({
  name: 'StatusChanger',
  components: {
    Status,
    Popconfirm,
    Dropdown,
    DownSvg,
    Menu,
    MenuItem,
    Tooltip,
    Loadable,
  },
  props: {
    currentState: {
      type: Object as PropType<TStatusChangerCurrentState>,
      default: () => ({}),
    },
    availableStates: {
      type: Array as PropType<TStatusChangerAvailableState[]>,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isDone: {
      type: Boolean,
      default: true,
    },
    tooltipText: {
      type: String,
      default: '',
    },
    textConfirm: {
      type: String,
      default: tt('shared.popconfirm.change', { param: { entity: toLowercase(tt('shared.status')) } }),
    },
    hasBackground: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String as PropType<ESize>,
      default: ESize.medium,
    },
  },
  emits: ['change', 'visibleChange'],
  setup(_, { emit, expose }) {
    const {
      isDropdownVisible,

      onDropdownVisibleChange,
    } = useDropdownVisibleChange({ emit });

    const handleChangeState = (newState: TStatusChangerAvailableState) => {
      if (newState.disabled) return;

      onDropdownVisibleChange(false);

      emit('change', newState);
    };

    expose({ onDropdownVisibleChange });

    return {
      isDropdownVisible,
      EPopoverPlacement,

      tt,
      toLowercase,
      onDropdownVisibleChange,
      handleChangeState,
    };
  },
});
</script>

<style lang='scss' src='./styles.scss' />
